import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../../schema.dexie";
import { getCriancaAtiva } from "../../db/queries";
import { listarAtividades, registrarMomentoCuidador } from "../queries";
import {
  CONTEXTOS_ORDEM,
  CONTEXTO_LABEL,
  type ContextoMomento,
  type EstadoRegulacao,
  type NivelAjuda,
  type PassoParticipacao,
} from "../types";
import { ParticipacaoSelector } from "../ui/ParticipacaoSelector";
import { AtividadeFoto } from "../ui/AtividadeFoto";
import { StatusAtividadeBadge } from "../ui/StatusAtividadeBadge";
import { RegulacaoSelector } from "../ui/RegulacaoSelector";
import { AjudaSelector } from "../ui/AjudaSelector";

/**
 * P0.2 — registro rápido (≤10s no caminho feliz): escolher atividade (recentes no
 * topo) → tocar o passo de participação → salvar. Nível de ajuda, regulação
 * antes/depois e contexto são OPCIONAIS e nunca bloqueiam.
 */
export function RegistrarMomentoPsm() {
  const { atividadeId } = useParams();
  const nav = useNavigate();
  const crianca = useLiveQuery(getCriancaAtiva);
  const atividades = useLiveQuery(() => listarAtividades()) ?? [];
  const momentos = useLiveQuery(() => db.momento_psm.toArray()) ?? [];

  const [selecionado, setSelecionado] = useState<string | null>(atividadeId ?? null);
  const [passo, setPasso] = useState<PassoParticipacao | null>(null);
  const [ajuda, setAjuda] = useState<NivelAjuda | null>(null);
  const [antes, setAntes] = useState<EstadoRegulacao | null>(null);
  const [depois, setDepois] = useState<EstadoRegulacao | null>(null);
  const [contexto, setContexto] = useState<ContextoMomento | "">("");
  const [salvo, setSalvo] = useState(false);

  // recentes no topo: pela data do último momento; depois por criação.
  const ordenadas = useMemo(() => {
    const ultimo = new Map<string, string>();
    for (const m of momentos) {
      const at = ultimo.get(m.atividade_id);
      if (!at || m.data_hora > at) ultimo.set(m.atividade_id, m.data_hora);
    }
    return [...atividades].sort((a, b) => {
      const ua = ultimo.get(a.id) ?? a.criado_em;
      const ub = ultimo.get(b.id) ?? b.criado_em;
      return ub.localeCompare(ua);
    });
  }, [atividades, momentos]);

  const atividade = atividades.find((a) => a.id === selecionado) ?? null;

  async function salvar() {
    if (!atividade || passo === null || !crianca) return;
    await registrarMomentoCuidador({
      atividade_id: atividade.id,
      crianca_id: crianca.id,
      passo_participacao: passo,
      nivel_ajuda: ajuda,
      estado_antes: antes,
      estado_depois: depois,
      contexto: contexto || null,
    });
    setSalvo(true);
    setTimeout(() => nav("/gingado", { replace: true }), 900);
  }

  if (salvo) {
    return (
      <main className="tela">
        <div className="vazio">
          <span className="emoji" aria-hidden>
            🤸
          </span>
          <div style={{ fontSize: "1.3rem", color: "var(--accent-forte)" }}>salvo!</div>
        </div>
      </main>
    );
  }

  // passo 1 — escolher atividade
  if (!atividade) {
    return (
      <main className="tela">
        <button className="btn btn-fantasma" onClick={() => nav(-1)}>
          ← voltar
        </button>
        <h1 className="tela-titulo">registrar momento</h1>
        <p className="tela-sub">qual atividade aconteceu?</p>
        {ordenadas.length === 0 ? (
          <div className="vazio">
            <span className="emoji" aria-hidden>
              🤸
            </span>
            cadastre uma atividade na biblioteca primeiro.
          </div>
        ) : (
          ordenadas.map((a) => (
            <button
              className="card linha-alimento"
              key={a.id}
              onClick={() => setSelecionado(a.id)}
            >
              <AtividadeFoto midias={a.midias} nome={a.nome} tipo={a.tipo} />
              <div className="crescer">
                <div className="nome">{a.nome}</div>
              </div>
              <StatusAtividadeBadge status={a.status} />
            </button>
          ))
        )}
      </main>
    );
  }

  // passo 2 — escada de participação + ajuda/regulação/contexto (opcionais)
  return (
    <main className="tela">
      <button
        className="btn btn-fantasma"
        onClick={() => (atividadeId ? nav(-1) : setSelecionado(null))}
      >
        ← trocar atividade
      </button>

      <div className="row" style={{ margin: "var(--s2) 0 var(--s4)" }}>
        <AtividadeFoto midias={atividade.midias} nome={atividade.nome} tipo={atividade.tipo} />
        <h1 className="tela-titulo" style={{ margin: 0 }}>
          {atividade.nome}
        </h1>
      </div>

      {atividade.seguranca && (
        <div className="callout-seguranca">
          <span aria-hidden>⚠️</span> {atividade.seguranca}
        </div>
      )}

      <h2 className="secao-titulo">como foi? toque o passo de participação</h2>
      <ParticipacaoSelector valor={passo} onChange={setPasso} />

      <section className="secao" style={{ marginTop: "var(--s5)" }}>
        <h2 className="secao-titulo">
          nível de ajuda <span style={{ color: "var(--text-suave)" }}>(opcional)</span>
        </h2>
        <div className="card">
          <AjudaSelector valor={ajuda} onChange={setAjuda} />
          <p className="status-regra">
            precisar de ajuda não é um passo pior — a ajuda decrescente no tempo é o
            próprio dado de autonomia.
          </p>
        </div>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">
          regulação tônica <span style={{ color: "var(--text-suave)" }}>(opcional)</span>
        </h2>
        <div className="card">
          <div className="campo">
            <label>antes</label>
            <RegulacaoSelector valor={antes} onChange={setAntes} ariaLabel="estado antes" />
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label>depois</label>
            <RegulacaoSelector valor={depois} onChange={setDepois} ariaLabel="estado depois" />
          </div>
        </div>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">
          contexto <span style={{ color: "var(--text-suave)" }}>(opcional)</span>
        </h2>
        <div className="card">
          <div className="campo" style={{ margin: 0 }}>
            <label>quando / onde</label>
            <select value={contexto} onChange={(e) => setContexto(e.target.value as ContextoMomento | "")}>
              <option value="">—</option>
              {CONTEXTOS_ORDEM.map((c) => (
                <option key={c} value={c}>
                  {CONTEXTO_LABEL[c]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <button
        className="btn btn-primario btn-bloco btn-grande"
        disabled={passo === null}
        onClick={salvar}
      >
        salvar momento
      </button>
    </main>
  );
}
